import { Box, Button, Center, VStack, Text, Icon, useColorModeValue } from "@chakra-ui/react";
import { googleLogout } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { RiErrorWarningLine } from "react-icons/ri";
import { useTranslation } from "react-i18next";

const AccountDisabled = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const pageBg = useColorModeValue(
    "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)",
    "linear-gradient(180deg, #0a0a0b 0%, #0d0d10 45%, #0a0a0b 100%)"
  );
  const shellBorder = useColorModeValue("rgba(15,23,42,0.1)", "rgba(255,255,255,0.12)");
  const shellBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(18,18,20,0.88)");
  const shellShadow = useColorModeValue(
    "0 8px 40px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
    "0 12px 48px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)"
  );
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.650");
  const iconTileBg = useColorModeValue("red.50", "rgba(239,68,68,0.12)");
  const iconTileBorder = useColorModeValue("red.100", "rgba(248,113,113,0.25)");
  const accentLine = useColorModeValue("linear(to-r, brand.400, brand.500)", "linear(to-r, brand.400, brand.500)");
  const dangerMuted = useColorModeValue("red.800", "red.300");
  const dotPatternOpacity = useColorModeValue(0.4, 0.25);
  const dotPatternBg = useColorModeValue(
    "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.06) 1px, transparent 0)",
    "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)"
  );

  const handleLogout = () => {
    googleLogout();
    localStorage.removeItem("user");
    navigate("/");
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      bg={pageBg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={12}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        opacity: dotPatternOpacity,
        pointerEvents: "none",
        backgroundImage: dotPatternBg,
        backgroundSize: "24px 24px",
      }}
    >
      <Center w="100%" maxW="420px" position="relative" zIndex={1}>
        <Box
          w="100%"
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={shellBorder}
          bg={shellBg}
          backdropFilter="blur(16px)"
          boxShadow={shellShadow}
          p={{ base: 6, md: 8 }}
          textAlign="center"
        >
          <Box
            mx="auto"
            w="56px"
            h="56px"
            borderRadius="xl"
            bg={iconTileBg}
            borderWidth="1px"
            borderColor={iconTileBorder}
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={5}
          >
            <Icon as={RiErrorWarningLine} w={8} h={8} color="red.500" />
          </Box>

          <Box h="3px" w="48px" borderRadius="full" bgGradient={accentLine} mx="auto" mb={4} opacity={0.85} />

          <Text
            as="h1"
            fontSize="xl"
            fontWeight="semibold"
            color={titleColor}
            letterSpacing="-0.02em"
            mb={2}
          >
            {t("accountDisabled.title")}
          </Text>
          <Text fontSize="sm" color={dangerMuted} fontWeight="medium" mb={4}>
            {t("accountDisabled.subtitle")}
          </Text>
          <Text color={muted} fontSize="sm" lineHeight="tall" mb={8}>
            {t("accountDisabled.body")}
          </Text>

          <VStack spacing={3} w="100%">
            <Button
              type="button"
              w="100%"
              size="md"
              fontWeight="semibold"
              borderRadius="full"
              bgGradient="linear(to-r, brand.300, brand.500)"
              color="gray.900"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
              boxShadow="0 2px 12px rgba(52,211,153,0.22), inset 0 1px 0 rgba(255,255,255,0.35)"
              _hover={{
                bgGradient: "linear(to-r, brand.400, brand.600)",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 20px rgba(52,211,153,0.28)",
              }}
              _active={{ transform: "translateY(0)" }}
              onClick={handleLogout}
            >
              {t("accountDisabled.logOut")}
            </Button>
            <Text fontSize="xs" color={muted}>
              {t("accountDisabled.signOutHint")}
            </Text>
          </VStack>
        </Box>
      </Center>
    </Box>
  );
};

export default AccountDisabled;
